import { FieldError, FieldErrors, Resolver } from 'react-hook-form';
import { Experience } from '../../../types/experience';
import { Step, useFormState } from './useFormState';
import { getStepSchema } from './schema';
import { mergeStepValues } from './utils';
import { useGetIndividualOwnerAssetsQuery } from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';

/**
 * Builds a nested react-hook-form field error from a Zod issue path.
 * Keeps the first error when multiple issues share the same path.
 */
const assignError = (
    errors: Record<string, any>,
    path: (string | number)[],
    error: FieldError,
) => {
    let target = errors;
    path.forEach((key, index) => {
        if (index === path.length - 1) {
            // keep the first issue per field
            if (!target[key]) target[key] = error;
            return;
        }
        if (typeof target[key] !== 'object' || target[key] === null) {
            target[key] = {};
        }
        target = target[key];
    });
};

/**
 * Creates a react-hook-form resolver that validates every experience form step
 * in sequence and merges successful parses into a cleaned save payload.
 *
 * Use this in the edit form (full experience in one `FormProvider`) instead of
 * a single Zod schema or `.and()` chain. On each validation pass it:
 * - runs each step schema from `getStepSchema` against the current form values
 * - maps Zod issues onto nested RHF field errors
 * - reports which steps failed via `onStepErrors` (for tab highlighting)
 * - on success, returns only fields owned by the step schemas (`mergeStepValues`),
 *   so metadata such as `id`, `owner`, and `createdAt` is not submitted
 *
 * Must be called within a `FormStateProvider` (reads `steps` from form state).
 *
 * @param onStepErrors - Called with the list of steps that failed validation.
 *   Prefer writing to a ref here if the resolver may re-run on change; update
 *   React state for tab UI from the form's submit/error handlers instead.
 * @returns A react-hook-form `Resolver<Experience>` for `useForm({ resolver })`.
 *
 * @example
 * const [failedSteps, setFailedSteps] = useState<Step[]>([]);
 * const resolver = useStepResolver(setFailedSteps);
 *
 * const methods = useForm<Experience>({
 *     defaultValues: experience,
 *     resolver,
 * });
 */
export function useStepResolver(onStepErrors: (failedSteps: Step[]) => void) {
    const loggedInUser = UserRole();
    const { steps } = useFormState();

    const { data: allAssets } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
    });

    const resolver: Resolver<Experience> = async (values) => {
        const errors: FieldErrors<Experience> = {};
        const failedSteps: Step[] = [];
        let payload: Record<string, unknown> = {};

        for (const step of steps) {
            const { schema } = getStepSchema(step, values, allAssets ?? []);
            const result = schema.safeParse(values);

            if (!result.success) {
                failedSteps.push(step);
                result.error.issues.forEach((issue) => {
                    assignError(errors as Record<string, any>, issue.path, {
                        type: issue.code,
                        message: issue.message,
                    });
                });
                continue;
            }

            payload = mergeStepValues(payload, result.data);
        }

        onStepErrors(failedSteps);

        return Object.keys(errors).length
            ? { values: {}, errors }
            : { values: payload as Experience, errors: {} };
    };

    return resolver;
}
